package com.siriusxm.unifiedcontent.console.aggregator

import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.siriusxm.pia.CapabilityQuery
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.unifiedcontent.entityTypeObj
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.put
import unifiedcontent.aggregator.PartialSchema

/**
 * The partials the current user can publish onto [entity] through the console, as Compose state.
 * Reads null while [entity] is loading or AVP has not answered yet, then the list (possibly empty).
 *
 * A partial is offered when:
 * 1. its schema supports the entity's type;
 * 2. the console producer (the producer whose Cognito client id is the console's `clientId`) allows it
 *    for that type. The aggregator would accept anything else with a 200 and then drop it
 *    downstream, so it is never offered; and
 * 3. AVP allows `UnifiedContentAggregator_PartialUpdate` for it, asked with the same context the
 *    aggregator will build from the submitted modification (see [partialUpdateCapability]).
 *
 * All candidate partials are checked in one batched AVP call. With no console producer in this
 * environment the list is empty, which is the safe reading: the aggregator would have nowhere UCP to
 * route the call.
 *
 * @param publishedEntityId the id the editor will put in the modification's `id`, a typed entity id.
 *   This, not the loaded entity's own id, is what the server sends to AVP as `entityId`.
 * @param entity the loaded entity, or null while it is loading
 */
@Composable
fun AggregatorService.rememberPublishablePartials(
    publishedEntityId: String?,
    entity: Entity?
): State<List<PartialSchema>?> {
    val producer = consoleProducer(producers, context.configuration.clientId)
    val entityType = entity?.data?.entityTypeObj

    // The partial schemas the console producer may publish onto this type, each paired with the AVP
    // question for publishing it onto this entity. The modification the editors build carries the
    // entity's id, type, and version, so the server resolves the same ancestors from the same entity.
    val candidates: List<Pair<PartialSchema, CapabilityQuery>> =
        remember(producer, partials, publishedEntityId, entity?.data) {
            if (producer == null || entity == null || entityType == null || publishedEntityId == null) {
                emptyList()
            } else {
                val modification = buildJsonObject {
                    put("id", publishedEntityId)
                    put("type", entity.type)
                    put("version", entity.version)
                }
                consolePartialsFor(producer, partials, entityType).mapNotNull { schema ->
                    partialUpdateCapability(schema.id, modification, entity.data)?.let { schema to it }
                }
            }
        }

    val decisions by context.rememberCapabilities(candidates.map { it.second })
    val loading = entity == null || publishedEntityId == null
    return remember(candidates, loading) {
        derivedStateOf {
            val answered = decisions
            when {
                loading || answered == null -> null
                else -> candidates.filter { (_, query) -> answered[query] == true }.map { it.first }
            }
        }
    }
}

/**
 * Whether the current user can publish [partialId] onto [entity] through the console, as Compose
 * state. Reads false until AVP answers, so a save control appears only once it is allowed and never
 * flashes for users who are not.
 *
 * True only when the console producer allows the partial for the entity's type (the downstream
 * ceiling) and AVP allows `UnifiedContentAggregator_PartialUpdate` with the context the aggregator will
 * build. The editors that use this put the catalog entity's own `id`, `type`, and `version` in the
 * modification, so [entity] supplies both the published id and the ancestors. Hide-only; the
 * aggregator decides on save.
 */
@Composable
fun AggregatorService.rememberCanPublishPartial(partialId: String, entity: Entity): State<Boolean> {
    val publishable by rememberPublishablePartials(entity.id, entity)
    return remember(partialId) {
        derivedStateOf { publishable.orEmpty().any { it.id == partialId } }
    }
}

/**
 * A partial the user may want to publish or remove: its id and the `modification` that would be
 * sent. Used to ask AVP about existing partials, such as the rows of an entity's Incoming tab.
 */
data class PartialRequest(val partialId: String, val modification: JsonObject)

/**
 * For each of [requests], whether the current user can publish it through the console, as Compose
 * state. Reads an empty set until AVP answers; then the requests that are allowed.
 *
 * A request is allowed when the console producer allows its partial for the modification's `type`
 * and AVP allows `UnifiedContentAggregator_PartialUpdate` with the context the aggregator builds from
 * that modification. [storedEntity] is the entity the server would read `show` / `channel` from. One
 * batched AVP call covers all the requests. Hide-only; the aggregator decides on submit.
 */
@Composable
fun AggregatorService.rememberAllowedPartialRequests(
    requests: List<PartialRequest>,
    storedEntity: JsonObject?
): State<Set<PartialRequest>> {
    val producer = consoleProducer(producers, context.configuration.clientId)
    val candidates: List<Pair<PartialRequest, CapabilityQuery>> = remember(producer, requests, storedEntity) {
        if (producer == null) {
            emptyList()
        } else {
            requests.mapNotNull { request ->
                val type = (request.modification["type"] as? JsonPrimitive)?.contentOrNull
                if (type == null || !producer.allowsPartial(request.partialId, type)) return@mapNotNull null
                partialUpdateCapability(request.partialId, request.modification, storedEntity)
                    ?.let { request to it }
            }
        }
    }
    val decisions by context.rememberCapabilities(candidates.map { it.second })
    return remember(candidates) {
        derivedStateOf {
            val answered = decisions.orEmpty()
            candidates.filter { (_, query) -> answered[query] == true }.map { it.first }.toSet()
        }
    }
}
